
// Crypto-strong random string. Falls back to Math.random only where the Web
// Crypto API is missing (very old browsers) so codes can't be enumerated.
function genId(len = 8) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  try {
    const bytes = new Uint8Array(len);
    crypto.getRandomValues(bytes);
    let s = "";
    for (let i = 0; i < len; i++) s += alphabet[bytes[i] % alphabet.length];
    return s;
  } catch {
    let s = "";
    while (s.length < len) s += Math.random().toString(36).slice(2);
    return s.slice(0, len).toUpperCase();
  }
}

// Escape untrusted text (group names, session titles) before it is interpolated
// into the print-sheet HTML built by string concatenation.
function escapeHtml(str) {
  return String(str == null ? "" : str)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
// LU/Azure federated accounts sometimes arrive with an empty top-level email
// (ADFS releases email in custom claims instead). Fall back to user_metadata
// so the app always has a usable email to display and match against.
function userEmail(user) { return user?.email || user?.user_metadata?.email || user?.user_metadata?.upn || user?.user_metadata?.preferred_username || ""; }
function scoreColor(pct, G) { return pct >= 0.7 ? G.accent : pct >= 0.4 ? G.gold : G.red; }

// Adressen studenterna går med via. QR-koden bär hela länken, så att en
// skanning öppnar tRAT:en direkt i stället för att bara visa koden som text.
function joinUrl(sessionId) {
  const origin = typeof window !== "undefined" && window.location ? window.location.origin : "";
  return `${origin}/session/${encodeURIComponent(sessionId)}`;
}

function friendlyError(error, lang) {
  const sv = lang === 'sv';
  if (!error) return null;
  const msg = error.message || String(error);
  if (msg.includes('network') || msg.includes('fetch') || msg.includes('Failed to fetch'))
    return sv ? 'Nätverksfel – kontrollera anslutningen.' : 'Network error – check your connection.';
  if (msg.includes('JWT') || msg.includes('auth') || msg.includes('token'))
    return sv ? 'Sessionen har gått ut – ladda om sidan.' : 'Session expired – please reload.';
  if (msg.includes('duplicate') || msg.includes('unique') || msg.includes('23505'))
    return sv ? 'Det finns redan något med det namnet.' : 'Something with that name already exists.';
  if (msg.includes('not found') || msg.includes('PGRST116'))
    return sv ? 'Hittades inte – kontrollera koden.' : 'Not found – check the code.';
  if (msg.includes('permission') || msg.includes('policy'))
    return sv ? 'Åtkomst nekad – du saknar behörighet.' : 'Access denied – insufficient permissions.';
  return sv ? 'Något gick fel. Försök igen.' : 'Something went wrong. Please try again.';
}

// ISO timestamp → value accepted by <input type="datetime-local"> (local time,
// no seconds/offset — an ISO string renders the input empty)
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function genGroupCode() {
  try {
    const b = new Uint32Array(1);
    crypto.getRandomValues(b);
    return String(1000 + (b[0] % 9000));
  } catch {
    return String(Math.floor(1000 + Math.random() * 9000));
  }
}

// En grupp identifieras av namnet utan hänsyn till versaler: "Grupp 1" som
// skrivs om till "grupp 1" är samma grupp och behåller sin kod.
const groupKey = name => String(name).toLowerCase();

function findGroupCode(codes, name) {
  if (codes[name]) return codes[name];
  const hit = Object.keys(codes).find(n => groupKey(n) === groupKey(name));
  return hit ? codes[hit] : undefined;
}

function buildGroupCodes(groups, existingCodes = {}) {
  const codes = {};
  for (const g of groups) {
    codes[g] = findGroupCode(existingCodes, g) || genGroupCode();
  }
  return codes;
}

// Samma gräns som anslutnings-RPC:n (length(p_group_name) > 80 nekas); ett
// längre namn skulle stå på kodbladet men aldrig gå att ansluta med.
const GROUP_NAME_MAX = 80;
const tooLongGroupNames = groups => groups.filter(g => [...g].length > GROUP_NAME_MAX);

// Grupplistan skrivs som fri text, en grupp per rad. Dubbletter skulle ge två
// kodbladssidor med samma namn (och bara en kod), så de rensas bort på samma
// sätt som gruppgeneratorn i wizarden gör: första stavningen vinner.
function parseGroupList(text) {
  const groups = [];
  const seen = new Set();
  for (const line of String(text || "").split("\n")) {
    const name = line.trim();
    if (!name || seen.has(groupKey(name))) continue;
    seen.add(groupKey(name));
    groups.push(name);
  }
  return groups;
}

// Naturlig ordning: "Grupp 2" före "Grupp 10", inte tvärtom som ren
// teckenjämförelse ger. Utan den hamnade grupper som lagts till i efterhand
// där de råkade skrivas in, vilket blir obegripligt på ett numrerat kodblad.
const groupCollator = new Intl.Collator("sv", { numeric: true, sensitivity: "base" });
function sortGroups(groups) {
  return [...groups].sort((a, b) => groupCollator.compare(a, b));
}

// Samma naturliga ordning för två gruppnamn ("Grupp 2" före "Grupp 10")
function compareGroupNames(a, b) {
  return groupCollator.compare(a || "", b || "");
}

// Vad en ändrad grupplista betyder för kodbladet: nya namn behöver en ny kod,
// borttagna faller bort, och de som står kvar behåller koden de redan fått
// (annars slutar redan utdelade kodblad att gälla).
function diffGroups(prevGroups = [], nextGroups = []) {
  const prev = new Set(prevGroups.map(groupKey));
  const next = new Set(nextGroups.map(groupKey));
  return {
    added: nextGroups.filter(g => !prev.has(groupKey(g))),
    removed: prevGroups.filter(g => !next.has(groupKey(g))),
  };
}

// Returnerar den utskrivna HTML:en, vilket testerna använder för att
// kontrollera sidornas ordning.
function openCodeSheet(sessionTitle, sessionId, groupCodes) {
  // Sidorna följer alltid gruppernas naturliga ordning, oavsett i vilken
  // ordning anroparen råkar ha byggt sin kodkarta.
  const entries = sortGroups(Object.keys(groupCodes)).map(g => [g, groupCodes[g]]);
  const siteUrl = 'trat.aktivtlarande.se';
  const safeTitle = escapeHtml(sessionTitle);
  const safeSessionId = escapeHtml(sessionId);

  const pages = entries.map(([group, code], i) =>
    '<div class="page' + (i < entries.length - 1 ? '' : ' last') + '">' +
      '<div class="sheet">' +
        '<div class="top-bar"></div>' +
        '<div class="content">' +
          /* header */
          '<div class="logo-row">' +
            '<div class="logo-icon">' +
              '<svg width="28" height="28" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="8" fill="#1d4ed8"/><path d="M8 12h16M8 16h12M8 20h14" stroke="#fff" stroke-width="2.2" stroke-linecap="round"/></svg>' +
            '</div>' +
            '<div class="logo-text">tRAT</div>' +
          '</div>' +
          (sessionTitle ? '<div class="session-title">' + safeTitle + '</div>' : '') +
          /* group name */
          '<div class="group-label">Er grupp&ensp;<span class="sep">&vert;</span>&ensp;Your group</div>' +
          '<div class="group-name">' + escapeHtml(group) + '</div>' +
          /* steps */
          '<div class="steps">' +
            '<div class="step">' +
              '<div class="step-num">1</div>' +
              '<div class="step-body">' +
                '<div class="step-label">G\u00e5 till&ensp;<span class="sep">&vert;</span>&ensp;Go to</div>' +
                '<div class="step-value url">' + siteUrl + '</div>' +
              '</div>' +
            '</div>' +
            '<div class="step">' +
              '<div class="step-num">2</div>' +
              '<div class="step-body">' +
                '<div class="step-label">Ange sessions\u2011ID&ensp;<span class="sep">&vert;</span>&ensp;Enter session ID</div>' +
                '<div class="step-value">' + safeSessionId + '</div>' +
              '</div>' +
            '</div>' +
            '<div class="step">' +
              '<div class="step-num">3</div>' +
              '<div class="step-body">' +
                '<div class="step-label">Ange er gruppkod&ensp;<span class="sep">&vert;</span>&ensp;Enter your group code</div>' +
                '<div class="step-value code">' + code + '</div>' +
              '</div>' +
            '</div>' +
          '</div>' +
          /* footer inside content to avoid page break */
          '<div class="footer">' + siteUrl + '</div>' +
        '</div>' +
      '</div>' +
    '</div>'
  ).join('');

  const docTitle = escapeHtml('Kodblad \u2013 ' + (sessionTitle || sessionId));
  const html = '<!DOCTYPE html><html lang="sv"><head><meta charset="UTF-8"><title>' + docTitle + '</title>' +
    '<link rel="preconnect" href="https://fonts.googleapis.com">' +
    '<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet">' +
    '<style>' +
    '*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }' +
    'html, body { width: 100%; height: 100%; }' +
    'body { font-family: "Plus Jakarta Sans", "Segoe UI", Arial, sans-serif; background: #f0f2f5; color: #101828; -webkit-print-color-adjust: exact; print-color-adjust: exact; }' +

    /* page = one printed page */
    '.page { width: 100%; height: 100vh; display: flex; align-items: center; justify-content: center; padding: 40px; page-break-after: always; overflow: hidden; }' +
    '.page.last { page-break-after: auto; }' +

    /* the card / sheet */
    '.sheet { position: relative; width: 100%; max-width: 480px; background: #ffffff; border-radius: 18px; overflow: hidden; box-shadow: 0 4px 24px rgba(16,24,40,0.08), 0 1px 3px rgba(16,24,40,0.04); }' +
    '.top-bar { height: 5px; background: linear-gradient(90deg, #1d4ed8 0%, #7c3aed 50%, #c88a0a 100%); }' +
    '.content { padding: 40px 40px 28px; }' +

    /* logo */
    '.logo-row { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }' +
    '.logo-icon { flex-shrink: 0; }' +
    '.logo-text { font-weight: 800; font-size: 20px; color: #1d4ed8; letter-spacing: 0.02em; }' +
    '.session-title { font-size: 14px; color: #667085; margin-bottom: 2px; margin-top: 2px; }' +

    /* group */
    '.group-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.14em; color: #98a2b3; margin-top: 28px; margin-bottom: 4px; }' +
    '.group-name { font-size: 26px; font-weight: 800; color: #101828; margin-bottom: 28px; }' +

    /* bilingual separator */
    '.sep { color: #d0d5dd; font-weight: 400; }' +

    /* steps */
    '.steps { display: flex; flex-direction: column; gap: 0; }' +
    '.step { display: flex; align-items: flex-start; gap: 16px; padding: 16px 0; border-top: 1px solid #e4e7ec; }' +
    '.step:last-child { border-bottom: 1px solid #e4e7ec; }' +
    '.step-num { flex-shrink: 0; width: 32px; height: 32px; border-radius: 50%; background: #eff4ff; color: #1d4ed8; font-weight: 800; font-size: 14px; display: flex; align-items: center; justify-content: center; margin-top: 2px; }' +
    '.step-body { flex: 1; min-width: 0; }' +
    '.step-label { font-size: 13px; color: #667085; margin-bottom: 5px; font-weight: 500; }' +
    '.step-value { font-size: 20px; font-weight: 700; letter-spacing: 0.04em; color: #101828; word-break: break-all; }' +
    '.step-value.url { color: #344054; }' +
    '.step-value.code { font-size: 40px; font-weight: 800; letter-spacing: 0.22em; color: #1d4ed8; }' +

    /* footer — inside content area */
    '.footer { text-align: center; font-size: 11px; color: #c0c5cd; padding-top: 20px; margin-top: 24px; letter-spacing: 0.04em; border-top: 1px solid #f2f4f7; }' +

    /* print */
    '@media print {' +
      'body { background: #fff; margin: 0; padding: 0; }' +
      '.page { padding: 60px 56px 40px; height: auto; display: block; }' +
      '.sheet { box-shadow: none; border-radius: 0; max-width: 100%; page-break-inside: avoid; }' +
      '.top-bar { height: 4px; }' +
      '.content { padding: 48px 56px 32px; }' +
      '.no-print { display: none !important; }' +
    '}' +
    '</style></head><body>' +
    pages +
    '<script>window.onload = function(){ window.print(); }<\/script>' +
    '</body></html>';

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  return html;
}

// ─────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────
function copyToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
  const el = document.createElement("textarea"); el.value = text; el.style.position = "fixed"; el.style.opacity = "0";
  document.body.appendChild(el); el.focus(); el.select();
  try { document.execCommand("copy"); } catch (e) {}
  document.body.removeChild(el); return Promise.resolve();
}

// ─────────────────────────────────────────
//  Radbrytningar i frågetext och återkoppling
// ─────────────────────────────────────────
// I lagrad text betyder "\n" ett nytt stycke (så behåller QPS-importen
// frågornas stycken). Text kopierad ur en PDF har däremot en hård radbrytning
// i slutet av varje rad, och då blev varje PDF-rad ett eget stycke.

// Vid visning: en rad som fortsätter med liten bokstav (ev. efter en
// startparentes eller ett citattecken) är en fortsättning, inte ett stycke.
// Lagar även text som redan sparats med PDF-radbrytningar.
function joinSoftWraps(text) {
  return String(text || "").replace(/[ \t]*\n[ \t]*(?=["'“‘«(]?\p{Ll})/gu, " ");
}

const SENTENCE_END_RX = /[.!?:…]["'”’»)]*$/;

// Vid inklistring: fogar ihop hårt radbrutna rader till stycken. Tomma rader
// är alltid styckegränser. Slutar de flesta rader utan skiljetecken är texten
// radbruten (PDF): då är bara en kort rad som avslutar en mening slutet på ett
// stycke. Annars är varje rad ett stycke (Word, Google Docs), och bara rader
// som fortsätter med liten bokstav fogas ihop.
function unwrapPastedText(text) {
  const blocks = String(text || "").replace(/\r\n?/g, "\n").split(/\n[ \t]*\n/);
  const paras = [];
  for (const block of blocks) {
    const lines = block.split("\n").map(l => l.trim()).filter(Boolean);
    if (!lines.length) continue;
    const endsSentence = lines.slice(0, -1).filter(l => SENTENCE_END_RX.test(l)).length;
    const hardWrapped = lines.length > 2 && endsSentence < (lines.length - 1) / 2;
    const maxLen = Math.max(...lines.map(l => l.length));
    let cur = lines[0];
    for (let i = 1; i < lines.length; i++) {
      const prev = lines[i - 1];
      const next = lines[i];
      const continues = /^["'“‘«(]?\p{Ll}/u.test(next);
      const breakHere = hardWrapped
        ? SENTENCE_END_RX.test(prev) && prev.length < 0.8 * maxLen && !continues
        : !continues;
      if (breakHere) { paras.push(cur); cur = next; }
      else cur += (cur.endsWith("-") ? "" : " ") + next;
    }
    paras.push(cur);
  }
  return paras.join("\n");
}

// onPaste för textfält med frågetext/återkoppling. Ingriper bara när det
// inklistrade innehåller radbrytningar; markören hamnar efter den inklistrade
// texten.
function pasteUnwrapped(e, setValue) {
  const pasted = e.clipboardData?.getData("text/plain");
  if (!pasted || !/[\r\n]/.test(pasted)) return;
  e.preventDefault();
  const el = e.target;
  const clean = unwrapPastedText(pasted);
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const value = el.value.slice(0, start) + clean + el.value.slice(end);
  setValue(value);
  const caret = start + clean.length;
  requestAnimationFrame(() => { try { el.setSelectionRange(caret, caret); } catch { /* fältet borta */ } });
}

export { compareGroupNames, joinUrl, joinSoftWraps, unwrapPastedText, pasteUnwrapped, genId, userEmail, scoreColor, friendlyError, isoToLocalInput, genGroupCode, buildGroupCodes, findGroupCode, GROUP_NAME_MAX, tooLongGroupNames, parseGroupList, sortGroups, diffGroups, openCodeSheet, copyToClipboard, escapeHtml };
